import { SceneType } from 'types/Scene';
import { DEFAULT_IMAGE_DURATION, MAX_IMAGE_DURATION } from 'constants/scenes';
import { call, select } from 'redux-saga/effects';
import getMediaDuration from 'utils/getMediaDuration';
import { getScene } from 'modules/scenes';
import { cancellableRequest } from 'utils/sagas/cancellable';
import { addContent } from 'utils/apiWrapper';
import { getFile, isCancellable } from 'utils/fileCache';
import { analyticsUploadDone } from 'utils/sagas/uploadingAnalytics';
import ErrorLogger from '../../components/ErrorLogger';

export enum ProgressParts {
  Uploading = 'uploading',
  Downloading = 'downloading',
  Preprocessing = 'preprocessing',
  ErrorHappened = 'errorHappened',
  TaskDone = 'taskDone'
}

export type LoadingProgress = {
  [ProgressParts.Downloading]: number;
  [ProgressParts.Uploading]: number;
  [ProgressParts.Preprocessing]: number;
  [ProgressParts.ErrorHappened]: boolean;
  [ProgressParts.TaskDone]: boolean;
};

export const createDefaultProgress = (): LoadingProgress => {
  return {
    uploading: 0,
    downloading: 0,
    preprocessing: 0,
    errorHappened: false,
    taskDone: false
  };
};

export type HandleLoadingProgress = (p: number) => void;

export const handleLoadingProgress = (
  progressObject: LoadingProgress,
  key: ProgressParts.Downloading | ProgressParts.Uploading | ProgressParts.Preprocessing
): HandleLoadingProgress => {
  return (p: number) => (progressObject[key] = p);
};

export function* getSceneDuration(sceneType: SceneType, localSrc: string, sceneId: string) {
  if (sceneType === SceneType.VIDEO) {
    const duration: number = yield call(getMediaDuration, localSrc, 'video');
    return [duration, duration];
  }

  if (sceneType === SceneType.IMAGE) {
    const { duration: currentDuration } = yield select(getScene(sceneId));
    return [currentDuration > 0 ? currentDuration : DEFAULT_IMAGE_DURATION, MAX_IMAGE_DURATION];
  }

  return [DEFAULT_IMAGE_DURATION, MAX_IMAGE_DURATION];
}

export function fileToSceneType(file: File): SceneType | boolean {
  let sceneType: SceneType | boolean = false;

  if (file.type.includes('video')) {
    sceneType = SceneType.VIDEO;
  } else if (file.type.includes('image')) {
    sceneType = SceneType.IMAGE;
  }

  return sceneType;
}

export function uploadToCDNRequest(
  file: File,
  userKey: string,
  handleProgress?: HandleLoadingProgress
) {
  return cancellableRequest<Promise<string>>(token =>
    addContent(file, userKey, handleProgress, token)
  );
}

export function downloadFromCDNRequest(url: string, handleProgress?: HandleLoadingProgress) {
  return cancellableRequest<Promise<string>>(
    cancelToken =>
      getFile(url, {
        handleProgress,
        handleError: () => {
          throw new Error();
        },
        cancelToken
      }),
    () => {
      return isCancellable(url);
    }
  );
}

export function* uploadToCDN(
  file: File,
  userKey: string,
  handleProgress: HandleLoadingProgress,
  sceneType: SceneType
) {
  try {
    const src: string = yield call(uploadToCDNRequest, file, userKey, handleProgress);

    if (!src) {
      throw new Error();
    }

    yield call(analyticsUploadDone, sceneType, 'success');

    return src;
  } catch (e) {
    ErrorLogger.trigger(e);
    yield call(analyticsUploadDone, sceneType, 'fail');
    throw new Error('failed to upload to CDN');
  }
}

export function* downloadFromCDN(url: string, handleProgress?: HandleLoadingProgress) {
  const src: string = yield call(downloadFromCDNRequest, url, handleProgress);

  if (!src) {
    throw new Error('failed to download from CDN');
  }

  return src;
}
